// The draft drawn formatted, in an editable element whose text is the draft itself: every
// character of the Markdown is in it, those that only format the rest hidden, so a place in the
// element is a place in the text. Lines are drawn afresh only when they change.
import {
  codeBlocks,
  hiddenRanges,
  lineAt,
  parseMarkdown,
  type Line,
  type MarkKind,
  type Span,
} from './markdown';

/** The draft's field: the formatted text, with a textarea's value and selection. */
export interface DraftField extends HTMLElement {
  value: string;
  /** Locked, as while dictating: there to read and select, not to change. */
  readonly readOnly: boolean;
  readonly selectionStart: number;
  readonly selectionEnd: number;
  readonly selectionDirection: 'forward' | 'backward' | 'none';
  setSelectionRange(start: number, end: number, direction?: 'forward' | 'backward' | 'none'): void;
  select(): void;
}

/** What's drawn over the writing besides its formatting. */
export interface Decorations {
  /** Words that look mistyped; `open` for the one whose suggestions are open. */
  readonly typos: readonly {
    readonly start: number;
    readonly end: number;
    readonly open: boolean;
  }[];
  /** What an AI tool is formatting, or just made. */
  readonly mark: {
    readonly start: number;
    readonly end: number;
    readonly state: 'working' | 'done';
    /** When it became so, by the page's clock, so it keeps time if it's drawn again. */
    readonly since?: number | undefined;
    /**
     * Writing that comes in at its own pace, as an image's does: how long from one word's
     * arrival to the next, as a lit caret writes it in (image-progress.ts).
     */
    readonly pace?: number | undefined;
  } | null;
  /** The letters of the word being typed, and after them, in grey, the rest of it. */
  readonly completion: {
    readonly start: number;
    readonly end: number;
    readonly rest: string;
  } | null;
  /** Stretches drawn as something of their own, such as a version's changes. */
  readonly ranges?: readonly { readonly start: number; readonly end: number }[] | undefined;
  /** What's drawn at places without being written there, such as words a version took out. */
  readonly extras?: readonly { readonly at: number }[] | undefined;
}

export const NO_DECORATIONS: Decorations = { typos: [], mark: null, completion: null };

/** A piece of a line, as it's drawn. */
export type Part =
  | {
      readonly type: 'text';
      readonly text: string;
      /** Formatting that only says how the rest reads, such as **. */
      readonly hidden: boolean;
      readonly className: string;
      readonly href?: string | undefined;
      /** Which of the decorations' ranges it's in. */
      readonly range?: number | undefined;
    }
  /** The rest of the word being typed, offered: drawn, but not written. */
  | { readonly type: 'rest'; readonly text: string }
  /** One of the decorations' extras, by its index: drawn, but not written. */
  | { readonly type: 'extra'; readonly index: number }
  /** What an AI tool is formatting, or just made, around the parts in it. */
  | {
      readonly type: 'mark';
      readonly state: 'working' | 'done';
      readonly parts: readonly Part[];
      /** How many marked words come before this line's, for animation in reading order. */
      readonly first: number;
      /** How many words it has in all, so the new text arrives in the same time, however long. */
      readonly words: number;
      readonly since: number;
      /** Its words' pace as they arrive, if they come in at their own; 0 if not. */
      readonly pace: number;
    }
  /** A table's cell, around the parts of its text. */
  | { readonly type: 'cell'; readonly parts: readonly Part[] };

/** A line, as it's drawn. */
export interface LineView {
  readonly kind: Line['kind'];
  readonly className: string;
  readonly depth: number;
  readonly number: string;
  readonly checked: boolean | null;
  /** A table row's columns, for its cells to line up with the rest of its table's. */
  readonly columns: number;
  readonly parts: readonly Part[];
  /** Nothing in it reads, so it needs something to hold its height. */
  readonly empty: boolean;
  /** The last line, with no line break after it. */
  readonly last: boolean;
}

const MARK_CLASS: Readonly<Record<Exclude<MarkKind, 'color' | 'highlight'>, string>> = {
  strong: 'md-strong',
  em: 'md-em',
  underline: 'md-underline',
  strike: 'md-strike',
  sub: 'md-sub',
  sup: 'md-sup',
  code: 'md-inline-code',
  link: 'md-link',
};

/** The class a span of formatting is drawn with: a color's names the color. */
function markClass(span: Span): string {
  if (span.kind === 'color') return `md-color md-color-${span.color ?? 'gray'}`;
  if (span.kind === 'highlight') return `md-highlight md-highlight-${span.color ?? 'yellow'}`;
  return MARK_CLASS[span.kind];
}

/** Whether any of a part reads, rather than only formatting the rest. */
const reads = (part: Part): boolean =>
  part.type === 'mark' || part.type === 'cell'
    ? part.parts.some(reads)
    : part.type === 'extra' || (part.type === 'text' && !part.hidden);

/** An extra, by its index in the decorations, and where it's drawn. */
interface Placed {
  readonly index: number;
  readonly at: number;
}

/**
 * The extras each line has, by the line's index. One at a line break is at the end of the line
 * before it; one in a line that isn't seen, such as under a table's header, is at the end of the
 * line before that.
 */
function extrasByLine(
  lines: readonly Line[],
  hidden: ReadonlySet<Line | undefined>,
  decorations: Decorations,
): Map<number, Placed[]> {
  const byLine = new Map<number, Placed[]>();
  for (const [index, { at }] of (decorations.extras ?? []).entries()) {
    let line = lines.indexOf(lineAt(lines, at));
    let place = at;
    while (line > 0 && (lines[line]?.kind === 'delimiter' || hidden.has(lines[line]))) {
      line -= 1;
      place = lines[line]?.end ?? place;
    }
    const placed = byLine.get(line) ?? [];
    placed.push({ index, at: place });
    byLine.set(line, placed);
  }
  for (const placed of byLine.values()) placed.sort((a, b) => a.at - b.at);
  return byLine;
}

/** How each line is drawn: its kind, and its text in parts of the same formatting. */
export function lineViews(
  text: string,
  lines: readonly Line[] = parseMarkdown(text),
  decorations: Decorations = NO_DECORATIONS,
): LineView[] {
  // A closed code block's fences only say where its code is, so they aren't shown.
  const fences = new Set(codeBlocks(lines).flatMap((block) => [block[0], block.at(-1)]));
  // Count marked words across lines for the working phrase groups and the finished text's
  // arrival sequence; formatting that doesn't show doesn't count.
  let seen = 0;
  const shown = (parts: readonly Part[]): number =>
    parts.reduce(
      (sum, part) =>
        sum +
        (part.type === 'text' && !part.hidden
          ? (part.text.match(/\S+/gu)?.length ?? 0)
          : part.type === 'cell' || part.type === 'mark'
            ? shown(part.parts)
            : 0),
      0,
    );
  const extras = extrasByLine(lines, fences, decorations);
  const drawn = lines.map((line, index) =>
    partsOf(text, line, decorations, extras.get(index) ?? []),
  );
  const marked = (parts: readonly Part[]): number =>
    parts.reduce(
      (sum, part) =>
        sum +
        (part.type === 'mark' ? shown(part.parts) : part.type === 'cell' ? marked(part.parts) : 0),
      0,
    );
  const words = drawn.reduce((sum, parts) => sum + marked(parts), 0);
  const numbered = (parts: readonly Part[]): Part[] =>
    parts.map((part) => {
      if (part.type === 'cell') return { ...part, parts: numbered(part.parts) };
      if (part.type !== 'mark') return part;
      const first = seen;
      seen += shown(part.parts);
      return { ...part, first, words };
    });
  return lines.map((line, index) => {
    const parts = numbered(drawn[index] ?? []);
    return {
      kind: line.kind,
      className: lineClass(line, fences.has(line)),
      depth: line.depth,
      number: line.number,
      checked: line.kind === 'task' ? line.checked : null,
      columns: line.kind === 'row' ? Math.max(line.columns, line.cells.length) : 0,
      parts,
      // A row's empty cells hold their own height.
      empty: line.kind !== 'row' && !parts.some(reads),
      last: index === lines.length - 1,
    };
  });
}

function lineClass(line: Line, hidden: boolean): string {
  const names = ['md-line', `md-${line.kind}`];
  if (line.kind === 'heading') names.push(`md-h${String(line.level)}`);
  if (line.kind === 'task' && line.checked) names.push('is-checked');
  if (line.kind === 'fence') names.push(line.opens ? 'is-opening' : 'is-closing');
  if (hidden) names.push('is-hidden');
  if (line.kind === 'row' && line.header) names.push('is-header');
  return names.join(' ');
}

/**
 * A line's parts; a row's, its pipes hidden between its cells, and its extras in the cell
 * nearest them, as anything else in the row would take a cell's place.
 */
function partsOf(text: string, line: Line, decorations: Decorations, extras: Placed[]): Part[] {
  if (line.kind !== 'row' || line.cells.length === 0)
    return partsIn(text, line, line.start, line.end, decorations, extras);
  const inCells = line.cells.map((): Placed[] => []);
  for (const extra of extras) {
    const index = Math.max(
      0,
      line.cells.findLastIndex((cell) => cell.from <= extra.at),
    );
    const cell = line.cells[index];
    if (cell)
      inCells[index]?.push({ ...extra, at: Math.min(Math.max(extra.at, cell.from), cell.to) });
  }
  const parts: Part[] = [];
  let at = line.start;
  for (const [index, cell] of line.cells.entries()) {
    parts.push(...partsIn(text, line, at, cell.from, decorations));
    parts.push({
      type: 'cell',
      parts: partsIn(text, line, cell.from, cell.to, decorations, inCells[index]),
    });
    at = cell.to;
  }
  parts.push(...partsIn(text, line, at, line.end, decorations));
  return parts;
}

/** The parts of a line between `start` and `end`, with the extras there, in order. */
function partsIn(
  text: string,
  line: Line,
  start: number,
  end: number,
  decorations: Decorations,
  extras: readonly Placed[] = [],
): Part[] {
  const raw = line.kind === 'code' || line.kind === 'fence';
  const hidden = raw ? [] : hiddenRanges(line);
  const spans = raw ? [] : line.spans;
  const typos = decorations.typos.filter((typo) => typo.start < end && typo.end > start);
  const completion = decorations.completion;
  const rest =
    completion?.rest && completion.end >= start && completion.end <= end ? completion : null;
  const mark =
    decorations.mark && decorations.mark.start <= end && decorations.mark.end >= start
      ? decorations.mark
      : null;
  const ranges = (decorations.ranges ?? [])
    .map((range, index) => ({ ...range, index }))
    .filter((range) => range.start < end && range.end > start);

  const cuts = new Set<number>([start, end]);
  const cut = (offset: number) => {
    if (offset > start && offset < end) cuts.add(offset);
  };
  for (const [from, to] of hidden) [from, to].forEach(cut);
  for (const span of spans) [span.from, span.to].forEach(cut);
  for (const typo of typos) [typo.start, typo.end].forEach(cut);
  if (completion) [completion.start, completion.end].forEach(cut);
  if (mark) [mark.start, mark.end].forEach(cut);
  for (const range of ranges) [range.start, range.end].forEach(cut);
  for (const extra of extras) cut(extra.at);
  const offsets = [...cuts].sort((a, b) => a - b);

  const inMark: Part[] = [];
  const parts: Part[] = [];
  const add = (part: Part, marked: boolean) => (marked ? inMark : parts).push(part);
  let markAt = -1;
  // The extras not yet drawn, each before the text at or after its place.
  let extra = 0;
  const addExtras = (before: number, marked: boolean) => {
    for (; extra < extras.length && (extras[extra]?.at ?? 0) <= before; extra += 1)
      add({ type: 'extra', index: extras[extra]?.index ?? 0 }, marked);
  };
  for (let index = 0; index < offsets.length - 1; index += 1) {
    const from = offsets[index] ?? start;
    const to = offsets[index + 1] ?? end;
    if (to <= from) continue;
    const isHidden = hidden.some(([a, b]) => a <= from && to <= b);
    const names: string[] = [];
    let href: string | undefined;
    const range = ranges.find((one) => one.start <= from && to <= one.end);
    if (!isHidden) {
      for (const span of spans) {
        if (span.from <= from && to <= span.to) {
          names.push(markClass(span));
          if (span.kind === 'link') href = span.href;
        }
      }
      const typo = typos.find((one) => one.start <= from && to <= one.end);
      if (typo) names.push(typo.open ? 'editor-typo is-open' : 'editor-typo');
      if (completion && completion.start <= from && to <= completion.end)
        names.push('editor-ghost-typed');
    }
    const marked = mark !== null && mark.start <= from && to <= mark.end;
    if (marked && markAt === -1) markAt = parts.length;
    addExtras(from, marked);
    add(
      {
        type: 'text',
        text: text.slice(from, to),
        hidden: isHidden,
        className: names.join(' '),
        href,
        ...(range && { range: range.index }),
      },
      marked,
    );
    if (rest?.end === to) add({ type: 'rest', text: rest.rest }, marked);
  }
  addExtras(end, false);
  // An empty line, or where the typing is at the line's start, can still have the rest offered.
  if (rest?.end === start && offsets.length <= 2 && start === end)
    parts.push({ type: 'rest', text: rest.rest });
  // Formatting alone, such as a row's pipes, isn't marked: drawn in an element of its own, it
  // would take a cell's place in its row.
  if (mark && inMark.length > 0) {
    parts.splice(
      markAt === -1 ? parts.length : markAt,
      0,
      ...(inMark.some(reads)
        ? [
            {
              type: 'mark' as const,
              state: mark.state,
              parts: inMark,
              // Numbered once the lines before it are known (lineViews).
              first: 0,
              words: 0,
              since: mark.since ?? 0,
              pace: mark.pace ?? 0,
            },
          ]
        : inMark),
    );
  }
  return parts;
}

// What each line element was drawn from, to tell whether it needs drawing again, and the draft
// each field was last drawn with.
const drawn = new WeakMap<Element, string>();
const drafts = new WeakMap<HTMLElement, string>();
const keyOf = (element: Element | undefined) => (element ? drawn.get(element) : undefined);

/** Where the marked words are in reading order, across lines and formatting. */
interface WordSequence {
  word: number;
}
/** Phrase spotlight's loop (input-panel.css), with four words sharing each highlight. */
const PHRASE_MS = 3600;
const PHRASE_WORDS = 4;

/**
 * `text`, each word on its own, for the text an AI tool marks to animate together, numbered in
 * reading order (input-panel.css); the spaces between stay plain, so lines wrap as they would.
 */
function markedWords(text: string, sequence: WordSequence): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const piece of text.split(/(\s+)/u)) {
    if (!piece) continue;
    if (/^\s+$/u.test(piece)) {
      fragment.append(piece);
      continue;
    }
    const word = document.createElement('span');
    word.className = 'editor-mark-word';
    word.style.setProperty('--word', String(sequence.word));
    word.style.setProperty('--phrase', String(Math.floor(sequence.word / PHRASE_WORDS)));
    sequence.word += 1;
    word.textContent = piece;
    fragment.append(word);
  }
  return fragment;
}

function partNode(part: Part, sequence: WordSequence | null = null): Node {
  if (part.type === 'cell') {
    const cell = document.createElement('span');
    cell.className = 'md-cell';
    cell.append(...part.parts.map((inner) => partNode(inner, sequence)));
    // An empty cell holds its height, and the caret, as an empty line does.
    if (!part.parts.some(reads)) cell.append(document.createElement('br'));
    return cell;
  }
  if (part.type === 'mark') {
    const wrapper = document.createElement('span');
    wrapper.className = `editor-mark is-${part.state}`;
    // Four-word groups share a highlight while a tool works, keeping time since it started,
    // even when a line is drawn again. Finished words arrive one by one as before.
    const words: WordSequence = { word: part.first };
    if (part.state === 'working') {
      wrapper.style.setProperty(
        '--phrases',
        String(Math.max(1, Math.ceil(part.words / PHRASE_WORDS))),
      );
      wrapper.style.setProperty(
        '--phrase-clock',
        `${String(Math.round(Math.max(0, performance.now() - part.since) % PHRASE_MS))}ms`,
      );
    } else {
      wrapper.style.setProperty('--words', String(Math.max(1, part.words)));
      wrapper.style.setProperty(
        '--arrived',
        `${String(Math.round(Math.max(0, performance.now() - part.since)))}ms`,
      );
      // Writing at its own pace is written in behind a lit caret, word by word, each lit.
      if (part.pace > 0) {
        wrapper.classList.add('is-paced');
        wrapper.style.setProperty('--word-step', `${String(part.pace)}ms`);
      }
    }
    wrapper.append(...part.parts.map((inner) => partNode(inner, words)));
    return wrapper;
  }
  // Only the versions draw extras (MarkdownText).
  if (part.type === 'extra') return document.createDocumentFragment();
  if (part.type === 'rest') {
    const rest = document.createElement('span');
    rest.className = 'editor-ghost-rest';
    rest.contentEditable = 'false';
    rest.dataset.widget = '';
    rest.setAttribute('aria-hidden', 'true');
    rest.textContent = part.text;
    return rest;
  }
  if (part.hidden) {
    const hidden = document.createElement('span');
    hidden.dataset.hidden = '';
    hidden.textContent = part.text;
    return hidden;
  }
  if (!part.className)
    return sequence ? markedWords(part.text, sequence) : document.createTextNode(part.text);
  const span = document.createElement('span');
  span.className = part.className;
  if (part.href) span.title = part.href;
  if (sequence) span.append(markedWords(part.text, sequence));
  else span.textContent = part.text;
  return span;
}

function lineNode(view: LineView): HTMLElement {
  const element = document.createElement('div');
  element.className = view.className;
  if (view.depth > 0) element.style.setProperty('--depth', String(view.depth));
  if (view.columns > 0) element.style.setProperty('--columns', String(view.columns));
  if (view.kind === 'bullet' || view.kind === 'ordered' || view.kind === 'task')
    element.dataset.depth = String(Math.min(view.depth, 2));
  if (view.number) element.dataset.number = view.number;
  if (view.checked !== null) {
    const box = document.createElement('span');
    box.className = 'md-check';
    box.contentEditable = 'false';
    box.dataset.widget = '';
    box.setAttribute('aria-hidden', 'true');
    element.append(box);
  }
  element.append(...view.parts.map((part) => partNode(part)));
  if (view.empty) element.append(document.createElement('br'));
  if (!view.last) {
    const eol = document.createElement('span');
    eol.dataset.hidden = '';
    eol.textContent = '\n';
    element.append(eol);
  }
  return element;
}

/**
 * Draws the draft in `root`, line by line, keeping the line elements that would be drawn the
 * same. Says whether it drew anything, which can take the selection with it.
 */
export function drawDraft(root: HTMLElement, text: string, decorations: Decorations): boolean {
  drafts.set(root, text);
  const views = lineViews(text, parseMarkdown(text), decorations);
  const keys = views.map((view) => JSON.stringify(view));
  const children = [...root.children];
  // Anything that isn't a line drawn here, as when the browser edited the text itself.
  const foreign =
    root.childNodes.length !== children.length || children.some((child) => !drawn.has(child));
  if (foreign) {
    root.replaceChildren(
      ...views.map((view, index) => {
        const element = lineNode(view);
        drawn.set(element, keys[index] ?? '');
        return element;
      }),
    );
    return true;
  }
  let head = 0;
  while (head < keys.length && head < children.length && keyOf(children[head]) === keys[head])
    head += 1;
  let tail = 0;
  while (
    tail < keys.length - head &&
    tail < children.length - head &&
    keyOf(children[children.length - 1 - tail]) === keys[keys.length - 1 - tail]
  )
    tail += 1;
  if (head === keys.length && head === children.length) return false;
  const stale = children.slice(head, children.length - tail);
  const fresh = views.slice(head, views.length - tail).map((view, index) => {
    const element = lineNode(view);
    drawn.set(element, keys[head + index] ?? '');
    return element;
  });
  const anchor = children[children.length - tail] ?? null;
  for (const element of stale) element.remove();
  for (const element of fresh) root.insertBefore(element, anchor);
  return true;
}

/** Forgets how the field was drawn, once the browser has changed it itself, to draw it afresh. */
export function forget(root: HTMLElement): void {
  for (const child of root.children) drawn.delete(child);
}

const isHiddenText = (node: Node) => node.parentElement?.closest('[data-hidden]') != null;

function textNodes(root: Node): Text[] {
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
    acceptNode: (node) =>
      node instanceof Element && node.hasAttribute('data-widget')
        ? NodeFilter.FILTER_REJECT
        : node.nodeType === Node.TEXT_NODE
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
  return nodes;
}

/**
 * The text in the field, as written: what the browser itself typed in, as an input method does,
 * included. A line it made of its own ends with a line break, as the lines drawn here do.
 */
export function readDraft(root: HTMLElement): string {
  const lines = [...root.childNodes];
  return lines
    .map((node, index) => {
      const written = textNodes(node)
        .map((text) => text.data)
        .join('');
      const own = node instanceof Element && drawn.has(node);
      return !own && node instanceof Element && index < lines.length - 1 && !written.endsWith('\n')
        ? `${written}\n`
        : written;
    })
    .join('');
}

/** How much of the draft a node holds: its text, less what's drawn but not written. */
const writtenLength = (node: Node) =>
  node instanceof Text ? node.length : textNodes(node).reduce((sum, text) => sum + text.length, 0);

/** The offset in the draft of a point in the field. */
export function offsetAt(root: HTMLElement, node: Node, offset: number): number {
  if (!root.contains(node)) return 0;
  const point = root.ownerDocument.createRange();
  try {
    point.setStart(node, offset);
  } catch {
    return 0;
  }
  let count = 0;
  for (const text of textNodes(root)) {
    if (text === node) return count + Math.min(offset, text.length);
    // The first text at or after the point.
    if (point.comparePoint(text, 0) >= 0) return count;
    count += text.length;
  }
  return count;
}

/**
 * The point in the field for an offset in the draft. Where the offset is at the edge of hidden
 * formatting, it's on the side that reads as the same place: the end of the text before it
 * first, so typing carries on its formatting, then the start of the text after.
 */
export function pointAt(root: HTMLElement, offset: number): [Node, number] {
  let count = 0;
  let starting: [Node, number] | null = null;
  for (const text of textNodes(root)) {
    const end = count + text.length;
    if (count > offset) break;
    if (offset <= end && !isHiddenText(text)) {
      if (offset > count) return [text, offset - count];
      starting ??= [text, 0];
    }
    count = end;
  }
  if (starting) return starting;
  // Nothing that reads is there, as in an empty line: in its line, before what holds its height.
  const text = drafts.get(root) ?? readDraft(root);
  const line = root.children[text.slice(0, offset).split('\n').length - 1];
  if (!line) return [root, root.childNodes.length];
  let at = text.lastIndexOf('\n', offset - 1) + 1;
  let index = 0;
  for (const child of line.childNodes) {
    const length = writtenLength(child);
    // An empty cell of a table holds the caret itself.
    if (child instanceof Element && child.classList.contains('md-cell') && !length && at === offset)
      return [child, 0];
    if (child.nodeName === 'BR' || at + length > offset) break;
    at += length;
    index += 1;
  }
  return [line, index];
}

/** The selection in the field, as [start, end, direction], or null when it isn't in it. */
export function selectionIn(root: HTMLElement): [number, number, 'forward' | 'backward'] | null {
  const selection = root.ownerDocument.getSelection();
  const { anchorNode, focusNode } = selection ?? {};
  if (
    !selection ||
    !anchorNode ||
    !focusNode ||
    !root.contains(anchorNode) ||
    !root.contains(focusNode)
  )
    return null;
  const anchor = offsetAt(root, anchorNode, selection.anchorOffset);
  const focus = offsetAt(root, focusNode, selection.focusOffset);
  return anchor <= focus ? [anchor, focus, 'forward'] : [focus, anchor, 'backward'];
}

/** Selects from `start` to `end` in the field. */
export function selectRange(
  root: HTMLElement,
  start: number,
  end: number,
  direction: 'forward' | 'backward' | 'none' = 'forward',
): void {
  const selection = root.ownerDocument.getSelection();
  if (!selection) return;
  const from = pointAt(root, start);
  const to = start === end ? from : pointAt(root, end);
  const [anchor, focus] = direction === 'backward' ? [to, from] : [from, to];
  const current = selectionIn(root);
  if (
    current?.[0] === start &&
    current[1] === end &&
    selection.anchorNode === anchor[0] &&
    selection.anchorOffset === anchor[1] &&
    selection.focusNode === focus[0] &&
    selection.focusOffset === focus[1]
  )
    return;
  try {
    selection.setBaseAndExtent(anchor[0], anchor[1], focus[0], focus[1]);
  } catch {
    // A point the browser won't take, in a line being drawn anew: it settles on the next draw.
  }
}

/**
 * Scrolls the field, and the page, as far as it takes for the caret to be in view, as typing in
 * a textarea does.
 */
export function revealCaret(root: HTMLElement): void {
  const selection = root.ownerDocument.getSelection();
  if (!selection?.rangeCount || !root.contains(selection.focusNode)) return;
  const range = document.createRange();
  try {
    range.setStart(selection.focusNode ?? root, selection.focusOffset);
  } catch {
    return;
  }
  if (typeof range.getBoundingClientRect !== 'function') return;
  let caret = range.getBoundingClientRect();
  // A caret before nothing, as on an empty line, has no box of its own: its line's will do.
  if (caret.height === 0) {
    const line = (
      selection.focusNode instanceof Element
        ? selection.focusNode
        : selection.focusNode?.parentElement
    )?.closest('.md-line');
    if (!line) return;
    caret = line.getBoundingClientRect();
  }
  const box = root.getBoundingClientRect();
  const padding = 8;
  if (caret.bottom > box.bottom - padding) root.scrollTop += caret.bottom - box.bottom + padding;
  else if (caret.top < box.top + padding) root.scrollTop -= box.top + padding - caret.top;
  const view = root.ownerDocument.defaultView;
  if (!view) return;
  const top = Math.max(caret.top, box.top);
  const bottom = Math.min(caret.bottom, box.bottom);
  if (bottom > view.innerHeight) view.scrollBy(0, bottom - view.innerHeight + padding);
  else if (top < 0) view.scrollBy(0, top - padding);
}
